import React, { useState } from 'react'
import { addYears, format } from 'date-fns'
import { Button } from 'components/ui/button'
import { Calendar } from 'components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import { it } from 'date-fns/locale'
import { CalendarIcon, Loader } from 'lucide-react'

interface PremiumExpirationPickerProps {
  value: string | null | undefined
  loading?: boolean
  /** Called once with the picked day as `yyyy-MM-dd`. */
  onChange: (date: string) => void
}

/**
 * Premium expiration of a user row. A calendar in a popover instead of a native
 * `<input type='date'>`: on iOS the native picker fires a change at every wheel
 * step (one API call each), while here the date is saved once, when picked.
 */
export const PremiumExpirationPicker: React.FC<
  PremiumExpirationPickerProps
> = ({ value, loading, onChange }) => {
  const [open, setOpen] = useState(false)
  const selected = value ? new Date(value) : undefined
  const today = new Date()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          disabled={loading}
          data-empty={!selected}
          className='min-w-36 justify-start font-normal data-[empty=true]:text-muted-foreground'
        >
          {selected ? format(selected, 'dd/MM/yyyy') : 'Nessuna'}
          {loading ? (
            <Loader className='ms-auto h-4 w-4 animate-spin' />
          ) : (
            <CalendarIcon className='ms-auto h-4 w-4 opacity-50' />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='single'
          required
          locale={it}
          captionLayout='dropdown'
          startMonth={new Date(2018, 0)}
          endMonth={addYears(today, 10)}
          defaultMonth={selected ?? today}
          selected={selected}
          onSelect={(date) => {
            setOpen(false)
            onChange(format(date, 'yyyy-MM-dd'))
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
